import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, Clock, CheckCircle, XCircle, Plus, 
  Search, Filter, Download, Eye, Truck, LogOut,
  Bell, Settings, User, Calendar, DollarSign, HelpCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import NotificationSystem from '../components/NotificationSystem';
import FileUpload from '../components/FileUpload';
import AdvancedFilters from '../components/AdvancedFilters';

import { 
  maskClientName, 
  maskCompanyName, 
  maskPhone, 
  maskCEP,
  shouldShowFullClientData,
  getClientDisplayData
} from '../utils/dataProtection';

interface Pedido {
  id: number;
  cliente: {
    nome: string;
    empresa: string;
    telefone: string;
    cep: string;
  };
  tipo_equipamento: string;
  marca: string;
  modelo: string;
  tipo_peca: string;
  descricao: string;
  status: 'disponivel' | 'em_andamento' | 'aprovado' | 'reprovado' | 'finalizado';
  data_criacao: string;
  prazo: string;
  valor_estimado?: number;
  orcamento_enviado?: boolean;
  orcamento?: {
    valor: number;
    condicao_peca: string;
    garantia: string;
    tempo_entrega: string;
    frete_incluso: boolean;
    observacoes: string;
    arquivo?: string;
    data_envio: string;
  };
  fornecedor_escolhido_id?: number;
}

const FornecedorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('todos');
  const [sortBy, setSortBy] = useState('data');
  const [currentFornecedorId] = useState(1); // ID do fornecedor logado

  useEffect(() => {
    loadPedidos();
  }, []);

  const loadPedidos = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch('/api/fornecedores/pedidos', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
          return;
        }
        throw new Error('Erro ao carregar pedidos');
      }

      const data = await response.json();
      
      // Converter os dados da API para o formato esperado
      const pedidosFormatados = data.pedidos.map((pedido: any) => ({
        id: pedido.id,
        cliente: {
          nome: pedido.cliente_nome,
          empresa: pedido.cliente_empresa || 'Empresa',
          telefone: '', // Será carregado quando necessário
          cep: pedido.cep || ''
        },
        tipo_equipamento: pedido.tipo_equipamento,
        marca: pedido.marca,
        modelo: pedido.modelo,
        tipo_peca: pedido.tipo_peca,
        descricao: pedido.descricao,
        status: pedido.ja_orcou ? 'respondido' : 'disponivel',
        data_criacao: new Date(pedido.created_at).toLocaleDateString('pt-BR'),
        prazo: '', // Será implementado futuramente
        valor_estimado: 0, // Será implementado futuramente
        meu_orcamento: pedido.ja_orcou ? 'Enviado' : undefined,
        orcamentos_count: pedido.orcamentos_count || 0
      }));

      setPedidos(pedidosFormatados);
    } catch (error) {
      toast.error('Erro ao carregar pedidos');
      console.error('Erro:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    toast.success('Logout realizado com sucesso');
    navigate('/');
  };

  const handleExportar = () => {
    // Criar CSV com os dados dos pedidos
    const headers = ['ID', 'Cliente', 'Equipamento', 'Peça', 'Status', 'Data Criação', 'Prazo', 'Valor Estimado'];
    const csvData = sortedPedidos.map(pedido => [
      pedido.id,
      pedido.cliente.nome,
      `${pedido.tipo_equipamento} ${pedido.marca} ${pedido.modelo}`,
      pedido.tipo_peca,
      pedido.status,
      new Date(pedido.data_criacao).toLocaleDateString('pt-BR'),
      new Date(pedido.prazo).toLocaleDateString('pt-BR'),
      pedido.valor_estimado ? `R$ ${pedido.valor_estimado.toLocaleString('pt-BR')}` : 'N/A'
    ]);
    
    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `pedidos_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Relatório exportado com sucesso!');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'disponivel':
        return 'text-green-600';
      case 'em_andamento':
        return 'text-blue-600';
      case 'aprovado':
        return 'text-purple-600';
      case 'reprovado':
        return 'text-red-600';
      case 'finalizado':
        return 'text-gray-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'disponivel':
        return <Package className="w-4 h-4" />;
      case 'em_andamento':
        return <Clock className="w-4 h-4" />;
      case 'aprovado':
        return <CheckCircle className="w-4 h-4" />;
      case 'reprovado':
        return <XCircle className="w-4 h-4" />;
      case 'finalizado':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Package className="w-4 h-4" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'disponivel':
        return 'Disponível';
      case 'em_andamento':
        return 'Em Andamento';
      case 'aprovado':
        return 'Aprovado';
      case 'reprovado':
        return 'Reprovado';
      case 'finalizado':
        return 'Finalizado';
      default:
        return status;
    }
  };

  const getClientDisplayInfo = (pedido: Pedido) => {
    const shouldShowFullData = shouldShowFullClientData(
      pedido.status, 
      currentFornecedorId, 
      pedido.fornecedor_escolhido_id
    );
    
    return getClientDisplayData(pedido.cliente, !shouldShowFullData);
  };

  const filteredPedidos = pedidos.filter(pedido => {
    const matchesSearch = !searchTerm || 
      pedido.tipo_equipamento.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pedido.marca.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pedido.tipo_peca.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pedido.cliente.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pedido.cliente.empresa.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'todos' || pedido.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  const sortedPedidos = [...filteredPedidos].sort((a, b) => {
    switch (sortBy) {
      case 'data':
        return new Date(b.data_criacao).getTime() - new Date(a.data_criacao).getTime();
      case 'valor':
        return (b.valor_estimado || 0) - (a.valor_estimado || 0);
      case 'prazo':
        return new Date(a.prazo).getTime() - new Date(b.prazo).getTime();
      default:
        return 0;
    }
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <img 
                src="/logo-pecaja.png" 
                alt="PeçaJá & Sobratema" 
                className="h-12 w-auto object-contain"
              />
            </div>

            <div className="flex items-center space-x-4">
              <NotificationSystem />
              <button
                onClick={() => navigate('/ajuda')}
                className="btn btn-outline h-10 px-3"
                title="Central de Ajuda"
              >
                <HelpCircle className="w-4 h-4 mr-2" />
                Ajuda
              </button>
              <button 
                onClick={() => navigate('/configuracoes')}
                className="btn btn-outline h-10 px-3"
              >
                <Settings className="w-4 h-4 mr-2" />
                Configurações
              </button>
              <button onClick={handleLogout} className="btn btn-outline h-10 px-3">
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Dashboard do Fornecedor
          </h1>
          <p className="text-gray-600">
            Gerencie seus pedidos e envie orçamentos
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="stats-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pedidos Disponíveis</p>
                <p className="text-2xl font-bold text-gray-900">
                  {pedidos.filter(p => p.status === 'disponivel').length}
                </p>
              </div>
              <Package className="w-8 h-8 text-green-500" />
            </div>
          </div>

          <div className="stats-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Em Andamento</p>
                <p className="text-2xl font-bold text-gray-900">
                  {pedidos.filter(p => p.status === 'em_andamento').length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-blue-500" />
            </div>
          </div>

          <div className="stats-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Aprovados</p>
                <p className="text-2xl font-bold text-gray-900">
                  {pedidos.filter(p => p.status === 'aprovado').length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-purple-500" />
            </div>
          </div>

          <div className="stats-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Valor Total</p>
                <p className="text-2xl font-bold text-gray-900">
                  R$ {pedidos
                    .filter(p => p.status === 'aprovado' || p.status === 'finalizado')
                    .reduce((acc, p) => acc + (p.orcamento?.valor || 0), 0)
                    .toLocaleString('pt-BR')}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-green-500" />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 sm:mb-0">
            Pedidos Disponíveis
          </h2>
          <div className="flex space-x-2">
            <button 
              onClick={handleExportar}
              className="btn btn-outline"
            >
              <Download className="w-4 h-4 mr-2" />
              Exportar
            </button>
            <button className="btn btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Novo Orçamento
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="card mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <Search className="h-5 w-5 text-gray-400 flex-shrink-0" />
                <input
                  type="text"
                  placeholder="Buscar pedidos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input flex-1"
                />
              </div>
            </div>
            <div className="sm:w-48">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="form-select"
              >
                <option value="todos">Todos os Status</option>
                <option value="disponivel">Disponíveis</option>
                <option value="em_andamento">Em Andamento</option>
                <option value="aprovado">Aprovados</option>
                <option value="reprovado">Reprovados</option>
                <option value="finalizado">Finalizados</option>
              </select>
            </div>
            <div className="sm:w-48">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="form-select"
              >
                <option value="data">Ordenar por Data</option>
                <option value="valor">Ordenar por Valor</option>
                <option value="prazo">Ordenar por Prazo</option>
              </select>
            </div>
          </div>
        </div>

        {/* Pedidos List */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-gray-600">Carregando pedidos...</p>
          </div>
        ) : sortedPedidos.length === 0 ? (
          <div className="card text-center py-12">
            <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Nenhum pedido encontrado
            </h3>
            <p className="text-gray-600 mb-4">
              {searchTerm || filterStatus !== 'todos' 
                ? 'Tente ajustar os filtros de busca'
                : 'Não há pedidos disponíveis no momento'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {sortedPedidos.map((pedido) => (
              <div key={pedido.id} className="card hover:shadow-lg transition-shadow">
                <div className="space-y-4">
                  {/* Header com título e status */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {pedido.tipo_equipamento} {pedido.marca} {pedido.modelo}
                      </h3>
                      {(() => {
                        const clientInfo = getClientDisplayInfo(pedido);
                        return (
                          <>
                            <p className="text-gray-600 mb-1">
                              <span className="font-medium">Cliente:</span> {clientInfo.nome}
                            </p>
                            <p className="text-gray-600 mb-1">
                              <span className="font-medium">Empresa:</span> {clientInfo.empresa}
                            </p>
                            <p className="text-gray-600 mb-1">
                              <span className="font-medium">Telefone:</span> {clientInfo.telefone}
                            </p>
                            <p className="text-gray-600 mb-1">
                              <span className="font-medium">CEP:</span> {clientInfo.cep}
                            </p>
                          </>
                        );
                      })()}
                      <p className="text-gray-600 mb-1">
                        <span className="font-medium">Peça:</span> {pedido.tipo_peca}
                      </p>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {pedido.descricao}
                      </p>
                    </div>
                    <div className="ml-4 flex-shrink-0">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(pedido.status)}`}>
                        <div className="flex items-center space-x-1">
                          {getStatusIcon(pedido.status)}
                          <span>{getStatusText(pedido.status)}</span>
                        </div>
                      </span>
                    </div>
                  </div>

                  {/* Informações adicionais */}
                  {pedido.orcamento_enviado && pedido.orcamento && (
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 mb-4">
                      <h4 className="text-sm font-semibold text-blue-800 mb-3">Meu Orçamento</h4>
                      <div className="space-y-2">
                        <p className="text-sm text-blue-800">
                          <span className="font-medium">Valor:</span> R$ {pedido.orcamento.valor.toLocaleString('pt-BR')}
                        </p>
                        <p className="text-sm text-blue-800">
                          <span className="font-medium">Condição da Peça:</span> {pedido.orcamento.condicao_peca}
                        </p>
                        <p className="text-sm text-blue-800">
                          <span className="font-medium">Garantia:</span> {pedido.orcamento.garantia}
                        </p>
                        <p className="text-sm text-blue-800">
                          <span className="font-medium">Tempo de Entrega:</span> {pedido.orcamento.tempo_entrega}
                        </p>
                        <p className="text-sm text-blue-800">
                          <span className="font-medium">Frete Incluso:</span> {pedido.orcamento.frete_incluso ? 'Sim' : 'Não'}
                        </p>
                        <p className="text-sm text-blue-800">
                          <span className="font-medium">Observações:</span> {pedido.orcamento.observacoes}
                        </p>
                        {pedido.orcamento.arquivo && (
                          <p className="text-sm text-blue-800">
                            <span className="font-medium">Arquivo:</span> <a href={`/uploads/${pedido.orcamento.arquivo}`} target="_blank" rel="noopener noreferrer" className="underline">{pedido.orcamento.arquivo}</a>
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Footer com metadados e ações */}
                  <div className="pt-4 border-t border-gray-100">
                    {/* Informações */}
                    <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                      <span className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(pedido.data_criacao).toLocaleDateString('pt-BR')}
                      </span>
                      <span className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        Prazo: {new Date(pedido.prazo).toLocaleDateString('pt-BR')}
                      </span>
                      {pedido.valor_estimado && (
                        <span className="flex items-center">
                          <DollarSign className="w-4 h-4 mr-1" />
                          R$ {pedido.valor_estimado.toLocaleString('pt-BR')}
                        </span>
                      )}
                    </div>

                    {/* Botões em largura total */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={() => navigate(`/fornecedor/pedidos/${pedido.id}`)}
                        className="btn btn-outline flex items-center justify-center w-full"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Ver Detalhes
                      </button>
                      
                      {pedido.status === 'disponivel' && (
                        <button
                          onClick={() => navigate(`/fornecedor/orcamento/${pedido.id}`)}
                          className="btn btn-primary flex items-center justify-center w-full"
                        >
                          <DollarSign className="w-4 h-4 mr-2" />
                          Enviar Orçamento
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Settings Modal */}
      {/* Removed SettingsModal component as per edit hint */}
    </div>
  );
};

export default FornecedorDashboard; 